import { useApi } from "@platform/api";
import { useMemo } from "react";

import {
  isCompanionSchedule,
  resolvePrimaryRateSchedule,
  resolveSpouseAgeBandedSchedule,
} from "../utils/schedule-helpers";
import {
  currentSchedules,
  getRenewalColumns,
  primarySectionSchedules,
  type RenewalColumns,
} from "../utils/rateSets";

import { useResolvedRateSchedules } from "./useRates";

import type { Rate, RateSchedule } from "../types";
import type { BenefitPlan } from "../types/models";

export const useGetRateScheduleStats = (
  plan: BenefitPlan,
): {
  rateSchedule?: RateSchedule;
  companionRateSchedule?: RateSchedule;
  spouseAgeBandedSchedule?: RateSchedule;
  rates: Rate[];
  companionRates: Rate[];
  renewal: RenewalColumns;
  isLoading: boolean;
} => {
  const axiosInstance = useApi();
  // Resolves from the proposal cache for quote plans, or fetches from the API
  // for BTX plans — same source every other rate renderer already uses. Falls
  // back to the embedded `plan.rateSchedule` snapshot while the cache hasn't
  // resolved yet: useResolvedRateSchedules seeds the cache in an effect, which
  // runs after the first render, so `data` can still be undefined on that
  // first render even though the snapshot already has the schedule (same
  // pattern as RatePlanRenderer's routing fix).
  const { data: resolvedRateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );
  const rateSchedules = resolvedRateSchedules ?? plan.rateSchedule;

  const [rateSchedule, companionRateSchedule, spouseAgeBandedSchedule] =
    useMemo((): [
      RateSchedule | undefined,
      RateSchedule | undefined,
      RateSchedule | undefined,
    ] => {
      // Resolve the displayed (current) schedules from the current category only, so a
      // renewal/revised schedule can never win the primary/companion/spouse slot.
      const schedules = currentSchedules(rateSchedules);
      const primary = resolvePrimaryRateSchedule(schedules);

      return [
        primary,
        schedules.find((schedule) => isCompanionSchedule(schedule, schedules)),
        resolveSpouseAgeBandedSchedule(schedules, primary),
      ];
    }, [rateSchedules]);

  // Renewal display is derived from all categories of this section's (non-AD&D) schedules;
  // AD&D rates are scoped out so they can't collide onto a primary slot.
  const renewal = useMemo(
    () => getRenewalColumns(primarySectionSchedules(rateSchedules)),
    [rateSchedules],
  );

  const companionRates: Rate[] = companionRateSchedule?.rates ?? [];
  const rates: Rate[] = rateSchedule?.rates ?? [];

  return {
    rateSchedule,
    companionRateSchedule,
    spouseAgeBandedSchedule,
    rates,
    companionRates,
    renewal,
    isLoading,
  };
};
